import { MutationTree } from 'vuex';
import { State } from './state';
import {
  ClientApiResponse,
  CollectionApiResponse,
  InteractionHistoryResponse,
  NextInteraction,
  YtdKpiTableApiResponseResults,
} from './types';
import { LinkableClient } from '@/types/SalesChurn';

export enum SalesChurnMutationTypes {
  SET_CLIENT_LOADING = 'SET_CLIENT_LOADING',
  SET_CLIENT = 'SET_CLIENT',
  SET_GROUP = 'SET_GROUP',
  SET_YTD_KPI_DATA_LOADING = 'SET_YTD_KPI_DATA_LOADING',
  SET_YTD_KPI_DATA = 'SET_YTD_KPI_DATA',
  SET_COLLECTIONS_LOADING = 'SET_COLLECTIONS_LOADING',
  SET_COLLECTIONS = 'SET_COLLECTIONS',
  SET_CLIENT_NEXT_INTERACTION = 'SET_CLIENT_NEXT_INTERACTION',
  RESET_SALES_CHURN_STATE = 'RESET_SALES_CHURN_STATE',
  SET_INTERACTION_ERROR = 'SET_INTERACTION_ERROR',
  SET_LINKABLE_CLIENTS = 'SET_LINKABLE_CLIENTS',
  RESET_LINKABLE_CLIENTS = 'RESET_LINKABLE_CLIENTS',
  SET_LINK_MODAL_LOADING = 'SET_LINK_MODAL_LOADING',
  SET_LINK_MODAL_SUBMITTING = 'SET_LINK_MODAL_SUBMITTING',
  SET_INTERACTION_HISTORY = 'SET_INTERACTION_HISTORY',
  SET_INTERACTION_HISTORY_LOADING = 'SET_INTERACTION_HISTORY_LOADING',
  RESET_INTERACTION_HISTORY = 'RESET_INTERACTION_HISTORY',
  TOGGLE_OPPORTUNITY_OPEN = 'TOGGLE_OPPORTUNITY_OPEN',
}

export type Mutations<S = State> = {
  [SalesChurnMutationTypes.SET_CLIENT_LOADING](
    state: S,
    payload: boolean
  ): void;
  [SalesChurnMutationTypes.SET_CLIENT](
    state: S,
    payload: ClientApiResponse
  ): void;
  [SalesChurnMutationTypes.SET_GROUP](
    state: S,
    payload: ClientApiResponse | null
  ): void;
  [SalesChurnMutationTypes.SET_YTD_KPI_DATA_LOADING](
    state: S,
    payload: boolean
  ): void;
  [SalesChurnMutationTypes.SET_YTD_KPI_DATA](
    state: S,
    payload: Array<YtdKpiTableApiResponseResults>
  ): void;
  [SalesChurnMutationTypes.SET_COLLECTIONS_LOADING](
    state: S,
    payload: boolean
  ): void;
  [SalesChurnMutationTypes.SET_COLLECTIONS](
    state: S,
    payload: CollectionApiResponse[]
  ): void;
  [SalesChurnMutationTypes.SET_CLIENT_NEXT_INTERACTION](
    state: S,
    payload: NextInteraction
  ): void;
  [SalesChurnMutationTypes.SET_INTERACTION_ERROR](
    state: S,
    payload: boolean
  ): void;
  [SalesChurnMutationTypes.RESET_SALES_CHURN_STATE](state: S): void;
  [SalesChurnMutationTypes.SET_LINKABLE_CLIENTS](
    state: S,
    payload: Array<LinkableClient>
  ): void;
  [SalesChurnMutationTypes.RESET_LINKABLE_CLIENTS](state: S): void;
  [SalesChurnMutationTypes.SET_LINK_MODAL_LOADING](
    state: S,
    payload: boolean
  ): void;
  [SalesChurnMutationTypes.SET_LINK_MODAL_SUBMITTING](
    state: S,
    payload: boolean
  ): void;
  [SalesChurnMutationTypes.SET_INTERACTION_HISTORY](
    state: S,
    payload: Array<InteractionHistoryResponse>
  ): void;
  [SalesChurnMutationTypes.SET_INTERACTION_HISTORY_LOADING](
    state: S,
    payload: boolean
  ): void;
  [SalesChurnMutationTypes.RESET_INTERACTION_HISTORY](state: S): void;
  [SalesChurnMutationTypes.TOGGLE_OPPORTUNITY_OPEN](
    state: S,
    payload: string
  ): void;
};

export const mutations: MutationTree<State> & Mutations = {
  [SalesChurnMutationTypes.SET_CLIENT_LOADING](state, payload) {
    state.loading = payload;
  },
  [SalesChurnMutationTypes.SET_CLIENT](state, payload) {
    state.client = payload;
  },
  [SalesChurnMutationTypes.SET_GROUP](state, payload) {
    state.group = payload;
  },
  [SalesChurnMutationTypes.SET_YTD_KPI_DATA_LOADING](state, payload) {
    state.ytdKpiDataIsLoading = payload;
  },
  [SalesChurnMutationTypes.SET_YTD_KPI_DATA](state, payload) {
    state.ytdKpiData = payload;
  },
  [SalesChurnMutationTypes.SET_COLLECTIONS_LOADING](state, payload) {
    state.collectionsLoading = payload;
  },
  [SalesChurnMutationTypes.SET_COLLECTIONS](state, payload) {
    state.collections = payload;
  },
  [SalesChurnMutationTypes.SET_CLIENT_NEXT_INTERACTION](state, payload) {
    state.nextInteraction = {
      userId: payload.userId,
      date: payload.date,
      dateString: payload.dateString,
      startTime: payload.startTime,
      endTime: payload.endTime,
      overdue: payload.overdue,
      type: payload.type,
      actions: payload.actions,
    };
  },
  [SalesChurnMutationTypes.SET_INTERACTION_ERROR](state, payload) {
    state.interactionError = payload;
  },
  [SalesChurnMutationTypes.RESET_SALES_CHURN_STATE](state) {
    state.loading = true;
    state.client = {} as ClientApiResponse;
    state.group = null;
    state.ytdKpiDataIsLoading = true;
    state.ytdKpiData = null;
    state.collections = [];
    state.collectionsLoading = true;
    state.nextInteraction = {
      userId: '-',
      date: '-',
      dateString: '',
      startTime: '-',
      endTime: '-',
      overdue: false,
      type: '',
      actions: [],
    };
    state.interactionError = false;
    state.opportunityOpen = {};
  },
  [SalesChurnMutationTypes.SET_LINKABLE_CLIENTS](state, linkableClients) {
    state.linkableClients = linkableClients;
  },
  [SalesChurnMutationTypes.RESET_LINKABLE_CLIENTS](state) {
    state.linkableClients = [];
  },
  [SalesChurnMutationTypes.SET_LINK_MODAL_LOADING](state, payload) {
    state.linkModalLoading = payload;
  },
  [SalesChurnMutationTypes.SET_LINK_MODAL_SUBMITTING](state, payload) {
    state.linkModalSubmitting = payload;
  },
  [SalesChurnMutationTypes.SET_INTERACTION_HISTORY](state, payload) {
    state.interactionHistory = payload;
  },
  [SalesChurnMutationTypes.SET_INTERACTION_HISTORY_LOADING](state, payload) {
    state.interactionHistoryLoading = payload;
  },
  [SalesChurnMutationTypes.RESET_INTERACTION_HISTORY](state) {
    state.interactionHistory = [];
  },
  [SalesChurnMutationTypes.TOGGLE_OPPORTUNITY_OPEN](state, taskId: string) {
    state.opportunityOpen[taskId] = !state.opportunityOpen[taskId];
  },
};
